import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../hooks/reduxHooks';
import { isOrderInvoiceSentToDagens } from '../../reducers/orders';
import ConsumerOrderPage from '_consumer/pages/order/ConsumerOrderPage';
import ProducerOrderPage from '_producer/pages/order/ProducerOrderPage';
import { Page } from '@components/page';
import { getOrderAndAdjustments, getOrders } from 'api';
import { Order, OrderKind } from 'types/Order';
import REQ, { ReqType } from 'utils/REQ';

const OrderPageFetcher = () => {
  const { accountId, accountType } = useAppSelector(
    ({ auth: { _id: accountId, _type: accountType } }) => {
      return {
        accountId,
        accountType
      };
    }
  );
  const [req, setReq] = useState<ReqType>(REQ.INIT);
  const [order, setOrder] = useState<Order>();
  const [orderAndAdjustments, setOrderAndAdjustments] = useState<OrderKind[]>(
    []
  );
  const { secret } = useParams<{ secret: string }>();

  const getOrder = async () => {
    setReq(REQ.PENDING);
    if (!secret) {
      setReq(REQ.ERROR);
      return;
    }

    try {
      // If the param is the order secret, use getOrders
      const orders = await getOrders({ secret });
      // If no orders are found for the secret, try to see if the param is the order number
      const order =
        orders.at(0) ?? (await getOrders({ orderNumber: secret })).at(0);

      if (!order) {
        setReq(REQ.NOT_FOUND);
      } else {
        setOrder(order);
        if (isOrderInvoiceSentToDagens(order)) {
          const { data } = await getOrderAndAdjustments(order._id, {
            accountType,
            accountId
          });
          setOrderAndAdjustments(data);
        }
        setReq(REQ.SUCCESS);
      }
    } catch (e) {
      setReq(REQ.ERROR);
    }
  };

  const onUpdateOrder = async () => {
    await getOrders({ secret })
      .then(orders => {
        setOrder(orders[0]);
      })
      .catch(() => {
        setReq(REQ.ERROR);
        throw new Error(`Could not update order line for secret ${secret}`);
      });
  };

  useEffect(() => {
    getOrder();
  }, [secret]);

  if (req !== REQ.SUCCESS) {
    return <Page.Status req={req} />;
  }
  if (!order) {
    return <Page.NotFound />;
  }

  return accountType === 'consumers' ? (
    <ConsumerOrderPage
      order={order}
      orderAndAdjustments={orderAndAdjustments}
    />
  ) : (
    <ProducerOrderPage
      order={order}
      orderAndAdjustments={orderAndAdjustments}
      onUpdateOrder={onUpdateOrder}
    />
  );
};

export default OrderPageFetcher;
